import { Dialog, Transition } from "@headlessui/react";
import { XIcon } from "@heroicons/react/outline";
import React, { Fragment, useRef } from "react";
import { useBillingPlans } from "../../context/billingPlans/BillingPlansProvider";
import { ModalConfig } from "../../context/toast/ToastProvider";
import { useWorkspaces } from "../../context/workspaces/WorkspacesProvider";
import PlansUpgrade from "../../pages/Plans/PlansUpgrade";
import { PlanType } from "../../utils/types";

interface Props {
  config: ModalConfig;
  setConfig: React.Dispatch<React.SetStateAction<ModalConfig>>;
}

const UpgradeHotspotsModal: React.FC<Props> = ({ config, setConfig }) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const { currentWorkspace } = useWorkspaces();
  const { newPlan, getPrePaidPlanByType, subscriptionPrePaid, setNewPlan } =
    useBillingPlans();

  return (
    <Transition.Root show={config.open} as={Fragment}>
      <Dialog
        as="div"
        open={config.open}
        className="fixed z-50 inset-0 overflow-y-auto"
        onClose={() => setConfig({ ...config, open: false })}
        initialFocus={cancelButtonRef}
      >
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-out duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full px-4 pb-4 sm:p-6 dark:bg-gray-800">
              <div className="block absolute top-0 right-0 pt-4 pr-4">
                <button
                  ref={cancelButtonRef}
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400 dark:bg-gray-800 dark:hover:text-white"
                  onClick={() => setConfig({ ...config, open: false })}
                >
                  <span className="sr-only">Close</span>
                  <XIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <Dialog.Title
                as="h3"
                className="text-lg mt-0 pt-0 font-medium text-gray-900 dark:text-white"
              >
                Update hotspots
              </Dialog.Title>

              <PlansUpgrade
                className="border-0 shadow-none rounded-t-none p-0 sm:p-0 mt-4"
                title="Hotspots"
                min={(currentWorkspace?.active_plan?.allowed_hotspots || 0) + 1}
                max={20000}
                subtitle={
                  <>
                    <div>
                      <b>{newPlan.hotspotAmount}</b> hotspot
                      {newPlan.hotspotAmount === 1 ? "" : "s"}
                    </div>
                    <div>
                      <b>
                        {(newPlan.hotspotAmount * 2).toLocaleString(undefined)}
                      </b>{" "}
                      locations
                    </div>
                  </>
                }
                disabledButton={
                  newPlan.hotspotAmount ===
                  currentWorkspace?.active_plan?.allowed_hotspots
                }
                buttonName="Upgrade"
                buttonNameLoading="Upgrading"
                amount={newPlan.hotspotAmount}
                value={newPlan.hotspotAmountPrice}
                priceTiers={
                  getPrePaidPlanByType(PlanType.prePaidHotspot)?.price_tiers
                }
                onClickButton={async () => {
                  await subscriptionPrePaid(PlanType.prePaidHotspot, 0, false);

                  config.resolve && config.resolve();

                  setConfig({
                    ...config,
                    open: false
                  });
                }}
                onChange={(value) =>
                  setNewPlan((prevState) => ({
                    ...prevState,
                    hotspotAmount: value
                  }))
                }
              />
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default UpgradeHotspotsModal;
